'use client';

import SectionCard from '@/app/(website)/components/SectionCard';
import SectionHeader from '@/app/(website)/components/SectionHeader';
import Book1 from '@/assets/icons/Book-1.svg';
import Book2 from '@/assets/icons/Book-2.svg';
import Details from '@/assets/icons/Details.svg';
import Scale from '@/assets/icons/Scale.svg';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

export default function SourcesCdl() {
  const sources = [
    {
      icon: <Scale width={64} height={64} />,
      title: 'Normativa',
      description: [
        <>
          <strong>Normativa Italiana:</strong> Leggi, decreti e regolamenti
          aggiornati quotidianamente, per garantire conformità e precisione
          nelle risposte.
        </>,
        <>
          <strong>Normativa Europea:</strong> Direttive e Regolamenti UE
          costantemente aggiornati, per assicurare conformità con il quadro
          normativo comunitario e le sue applicazioni negli Stati membri.
        </>,
      ],
    },
    {
      icon: <Book1 width={64} height={64} />,
      title: 'Giurisprudenza',
      description: [
        <>
          <strong>
            Giurisprudenza di Legittimità, Civile, Penale e Amministrativa e
            Tributaria di merito:
          </strong>{' '}
          Tutte le sentenze dalla Corte di Cassazione degli ultimi 5 anni e la
          Giurisprudenza Tributaria di merito.
        </>,
        <>
          <strong>Giurisprudenza della corte Europea:</strong> Sentenze e
          decisioni della Corte di Giustizia dell'Unione Europea (CGUE) e della
          Corte Europea dei Diritti dell'Uomo (CEDU), per un'interpretazione
          aggiornata del diritto comunitario e della tutela dei diritti
          fondamentali.
        </>,
      ],
    },
    {
      icon: <Book2 width={64} height={64} />,
      title: 'Prassi',
      description: (
        <>
          <strong>
            Prassi dell'Agenzia delle Entrate, INPS, INAIL, Agenzia delle Dogane, Ministero del Lavoro, MIT, ANAC:
          </strong>{' '}
          Circolari, Consulenze, Provvedimenti, Delibere e Linee Guida, Atti regolatori.
        </>
      ),
    },
    {
      icon: <Details width={64} height={64} />,
      title: 'Contrattazione Collettiva',
      description: (
        <>
          Integrazione di tutti i Contratti Collettivi del Lavoro Nazionali, provinciali e territoriali. Consultazione e supporto sull’applicazione della normativa giuslavoristica.
        </>
      ),
    },
    {
      icon: (
        <Image
          width={160}
          height={128}
          src={'/ec_logo.png'}
          alt="Dottrina Euroconference"
        />
      ),
      title: 'Dottrina Euroconference in Materia Giuslavoristica',
      description:
        "Guide operative, note esplicative, articoli specialistici e approfondimenti d'autore in materia fiscale, tributaria e giuslavoristica.",
    },
  ];
  const [isVisible, setIsVisible] = useState(false);
  const gridRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
      },
    );

    if (gridRef.current) {
      observer.observe(gridRef.current);
    }

    return () => {
      if (gridRef.current) {
        observer.unobserve(gridRef.current);
      }
    };
  }, []);

  const getClass = (index: number) => {
    if (index === 0) {
      return `transition-all duration-1000 ease-in-out transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40'
      }`;
    }
    if (index === 1) {
      return `transition-all duration-1000 ease-in-out transform delay-200 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40'
      }`;
    }
    if (index === 2) {
      return `transition-all duration-1000 ease-in-out transform delay-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40'
      }`;
    }

    if (index === 3) {
      return `transition-all duration-1000 ease-in-out transform delay-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40'
      }`;
    }

    return '';
  };

  return (
    <div id="sources" className="flex flex-col items-center gap-10 py-16 px-8">
      <SectionHeader
        badgeText="Fonti"
        title={
          <>
            Fonti{' '}
            <span className="rounded-[2rem] rounded-br-[8px] py-0 px-2 md:px-4 bg-secondary text-primary">
              informative
            </span>{' '}
            rilevanti per il lavoro del consulente del lavoro
          </>
        }
        subtitle="Normo.ai utilizza fonti normative, di prassi e giurisprudenziali per offrire una base di lavoro affidabile, aggiornata e utile nella pratica professionale."
        titleAnimationStart="bottom"
      />
      <div
        className="grid grid-cols-1 md:grid-cols-2 max-w-[912px] gap-8"
        ref={gridRef}
      >
        {sources.map((source, index) => (
          <SectionCard
            icon={source.icon}
            title={source.title}
            text={source.description}
            key={index}
            className={cn(
              getClass(index),
              index === sources.length - 1 &&
                sources.length % 2 === 1 &&
                'md:col-span-2',
            )}
          />
        ))}
      </div>
    </div>
  );
}
